// Экраны мобильной версии — слои над списком чатов: открытая переписка и
// поверх неё карточка человека. Каждый слой — запись в истории браузера, чтобы
// «Назад» (кнопка Android, жест iOS, стрелка в шапке) закрывал верхний слой,
// а не выходил из приложения. Слои живут в адресе: `?chat=<key>&info=1`.
//
// ChatsPage держит «что открыто» в своём состоянии и синхронизирует его с
// адресом в обе стороны; решения — здесь, чистыми функциями.

export const CHAT_PARAM = "chat";
export const PANEL_PARAM = "info";

export interface Layers {
  chat: string | null;
  panel: boolean;
}

export const NO_LAYERS: Layers = { chat: null, panel: false };

export function layersFromParams(sp: URLSearchParams): Layers {
  const chat = sp.get(CHAT_PARAM) || null;
  return { chat, panel: !!chat && sp.get(PANEL_PARAM) === "1" };
}

/** Те же параметры с другими слоями; чужие ключи (фильтры списка) — как были. */
export function withLayers(sp: URLSearchParams, l: Layers): URLSearchParams {
  const next = new URLSearchParams(sp);
  if (l.chat) next.set(CHAT_PARAM, l.chat);
  else next.delete(CHAT_PARAM);
  if (l.chat && l.panel) next.set(PANEL_PARAM, "1");
  else next.delete(PANEL_PARAM);
  // `?person=` — прыжок из Ctrl+K полной версии; здесь он уже стал `chat`.
  next.delete("person");
  return next;
}

export function depth(l: Layers): number {
  return l.chat ? (l.panel ? 2 : 1) : 0;
}

export function sameLayers(a: Layers, b: Layers): boolean {
  return a.chat === b.chat && a.panel === b.panel;
}

/** Слои из состояния страницы: карточка бывает только над открытым чатом. */
export function wantedLayers(selectedKey: string | null, panelOpen: boolean): Layers {
  return { chat: selectedKey, panel: !!selectedKey && panelOpen };
}

/** Как записать новые слои в адрес: глубже, чем в адресе, — новой записью
 *  истории (её снимет «Назад»), иначе — заменой текущей. `pushed` — сколько
 *  слоёв из адреса открыто нами поверх записи, с которой приложение начало:
 *  столько раз «Назад» ещё остаётся внутри него. */
export function writeStep(
  url: Layers,
  want: Layers,
  pushed: number,
): { replace: boolean; pushed: number } {
  const from = depth(url);
  const to = depth(want);
  if (to > from) return { replace: false, pushed: pushed + (to - from) };
  return { replace: true, pushed: Math.max(0, Math.min(pushed, to)) };
}

/** Адрес сменился сам — «Назад» / «Вперёд» браузера: столько слоёв теперь
 *  открыто нами. */
export function popStep(prev: Layers, next: Layers, pushed: number): number {
  return Math.max(0, pushed + depth(next) - depth(prev));
}

/** Закрыть верхний слой из интерфейса: если он наш — шагом «Назад» (запись
 *  истории уходит вместе с ним), иначе — просто закрыть (адрес заменится). */
export function closeByHistory(pushed: number): boolean {
  return pushed > 0;
}

// Карточка кандидата, открытая из карточки человека, просит «в чат» — а чат
// с ним и так под ней. Новой записью его не открыть (под ней осталась бы вся
// стопка), поэтому: карточка закрывается шагом назад, а когда закроется,
// ChatsPage закрывает и карточку человека — своим шагом, со своим счётом
// записей. Здесь — только заявка «кого показать».
let chatReturn: string | null = null;

export function requestChatReturn(key: string) {
  chatReturn = key;
}

/** Карточка закрылась: была ли заявка вернуться в переписку `key`. Заявка
 *  одноразовая — любая проверка её снимает. */
export function takeChatReturn(key: string | null): boolean {
  const want = chatReturn;
  chatReturn = null;
  return want != null && want === key;
}
